import React from "react";
import {
  Grid,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Switch,
  Stack,
  Typography,
  Divider,
  Box,
} from "@mui/material";
import LocalHospitalIcon from "@mui/icons-material/LocalHospital";
import MonetizationOnIcon from "@mui/icons-material/MonetizationOn";
import ShieldIcon from "@mui/icons-material/Shield";
import ElderlyIcon from "@mui/icons-material/Elderly";

/**
 * Initial state factory for PCP config form.
 */
export const buildPcpConfigInitialState = (existing = null) => ({
  title: existing?.title ?? "",
  highRisk: existing?.highRisk ?? false,
  highCost: existing?.highCost ?? false,
  medicare: existing?.medicare ?? false,
  critical: existing?.critical ?? false,
  ageMin: existing?.ageMin ?? "",
  ageMax: existing?.ageMax ?? "",
  maxPatient: existing?.maxPatient ?? "",
  daysWork: existing?.daysWork ?? "",
  employeeSupportId: existing?.employeeSupportId ?? "",
});

/**
 * Validates the PCP config form values. Returns first error message or null.
 * Mirrors backend validations from createPcpConfig / updatePcpConfig.
 */
export const validatePcpConfig = (form) => {
  const ageMin = form.ageMin === "" ? null : Number(form.ageMin);
  const ageMax = form.ageMax === "" ? null : Number(form.ageMax);
  const bothNull = ageMin === null && ageMax === null;
  const bothFilled = ageMin !== null && ageMax !== null;
  if (!bothNull && !bothFilled) {
    return "Age range: both MIN and MAX must be empty or both must have values.";
  }
  if (bothFilled && ageMin > ageMax) {
    return "Age MIN must be less than or equal to Age MAX.";
  }
  if (form.daysWork !== "" && (Number(form.daysWork) < 0 || Number(form.daysWork) > 7)) {
    return "Days/week must be between 0 and 7.";
  }
  if (form.maxPatient !== "" && Number(form.maxPatient) < 0) {
    return "Max patients cannot be negative.";
  }
  return null;
};

/**
 * Converts the form state into the payload expected by the backend
 * (`createPcpConfig` or `updatePcpConfig`). Empty strings → null.
 */
export const buildPcpConfigPayload = (form) => ({
  title: form.title?.trim() || null,
  highRisk: Boolean(form.highRisk),
  highCost: Boolean(form.highCost),
  medicare: Boolean(form.medicare),
  critical: Boolean(form.critical),
  ageMin: form.ageMin === "" ? null : Number(form.ageMin),
  ageMax: form.ageMax === "" ? null : Number(form.ageMax),
  maxPatient: form.maxPatient === "" ? null : Number(form.maxPatient),
  daysWork: form.daysWork === "" ? null : Number(form.daysWork),
  employeeSupportId:
    form.employeeSupportId === "" ? null : Number(form.employeeSupportId),
});

const ACCEPT_FLAGS = [
  { key: "highRisk", label: "High Risk", icon: <ShieldIcon sx={{ fontSize: 16 }} />, color: "#EF4444" },
  { key: "highCost", label: "High Cost", icon: <MonetizationOnIcon sx={{ fontSize: 16 }} />, color: "#F59E0B" },
  { key: "medicare", label: "Medicare", icon: <ElderlyIcon sx={{ fontSize: 16 }} />, color: "#3B82F6" },
  { key: "critical", label: "Critical", icon: <LocalHospitalIcon sx={{ fontSize: 16 }} />, color: "#8B5CF6" },
];

/**
 * Reusable fragment of PCP config fields. Used in:
 *  - EmployeeFormDialog (create flow, when toggling "is PCP")
 *  - PcpConfigDialog "Edit current" tab
 *  - PcpConfigDialog "Add to new location" tab
 *
 * @param {Object} props
 * @param {Object} props.value - form state from buildPcpConfigInitialState
 * @param {Function} props.onChange - (field, value) => void
 * @param {Array} [props.supportPcpOptions] - active PCPs available as backup ([{ employeePcpId, pcpFullName | name, ... }])
 */
export default function PcpConfigFormFields({ value, onChange, supportPcpOptions = [] }) {
  const set = (field) => (e) => onChange(field, e.target.value);
  const setBool = (field) => (e) => onChange(field, e.target.checked);

  return (
    <Stack spacing={2}>
      <Grid container spacing={2}>
        <Grid size={{ xs: 12, sm: 6 }}>
          <TextField
            label="Title"
            placeholder="MD, APRN, DO..."
            value={value.title}
            onChange={set("title")}
            fullWidth
            size="small"
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6 }}>
          <FormControl fullWidth size="small">
            <InputLabel>Backup PCP</InputLabel>
            <Select
              value={value.employeeSupportId}
              label="Backup PCP"
              onChange={set("employeeSupportId")}
            >
              <MenuItem value="">None</MenuItem>
              {supportPcpOptions.map((p) => (
                <MenuItem key={p.employeePcpId} value={p.employeePcpId}>
                  {p.pcpFullName || p.fullName || `${p.firstName || ""} ${p.lastName || ""}`.trim()}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
      </Grid>

      <Box>
        <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.5 }}>
          Patient types accepted
        </Typography>
        <Divider sx={{ mt: 0.5, mb: 1 }} />
        <Grid container spacing={1}>
          {ACCEPT_FLAGS.map((f) => (
            <Grid size={{ xs: 6, sm: 3 }} key={f.key}>
              <FormControlLabel
                control={
                  <Switch
                    checked={Boolean(value[f.key])}
                    onChange={setBool(f.key)}
                    sx={{
                      "& .MuiSwitch-thumb": { color: value[f.key] ? f.color : undefined },
                      "& .Mui-checked + .MuiSwitch-track": { bgcolor: `${f.color} !important` },
                    }}
                  />
                }
                label={
                  <Stack direction="row" spacing={0.5} alignItems="center">
                    <Box sx={{ color: f.color, display: "flex" }}>{f.icon}</Box>
                    <Typography variant="body2" fontWeight={600}>{f.label}</Typography>
                  </Stack>
                }
              />
            </Grid>
          ))}
        </Grid>
      </Box>

      <Box>
        <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.5 }}>
          Capacity & age
        </Typography>
        <Divider sx={{ mt: 0.5, mb: 1 }} />
        <Grid container spacing={2}>
          <Grid size={{ xs: 6, sm: 3 }}>
            <TextField
              label="Age MIN"
              value={value.ageMin}
              onChange={set("ageMin")}
              type="number"
              fullWidth
              size="small"
              helperText="Empty = any"
            />
          </Grid>
          <Grid size={{ xs: 6, sm: 3 }}>
            <TextField
              label="Age MAX"
              value={value.ageMax}
              onChange={set("ageMax")}
              type="number"
              fullWidth
              size="small"
              helperText="Empty = any"
            />
          </Grid>
          <Grid size={{ xs: 6, sm: 3 }}>
            <TextField
              label="Max patients"
              value={value.maxPatient}
              onChange={set("maxPatient")}
              type="number"
              fullWidth
              size="small"
              helperText="Empty = no limit"
            />
          </Grid>
          <Grid size={{ xs: 6, sm: 3 }}>
            <TextField
              label="Days / week"
              value={value.daysWork}
              onChange={set("daysWork")}
              type="number"
              fullWidth
              size="small"
              inputProps={{ min: 0, max: 7 }}
              helperText="0 – 7"
            />
          </Grid>
        </Grid>
      </Box>
    </Stack>
  );
}
